<template>
	<button type="button" class="button-flat" :class="[`button-flat--${variant}`, { 'button-flat--loading': loading }]" :disabled="disabled || loading" @click="$emit('buttonclick')">
		<span v-if="$slots.icon" class="button-flat__icon"><slot name="icon" /></span>
		<span class="button-flat__label"><slot /></span>
	</button>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

/**
 * Button nach der Figma-Vorgabe: 6px Radius, 16px/9px Innenabstand, Roboto Medium 16px.
 * Bewusst kein ElButton-Wrapper wie TheButton: ElButton erzwingt Pill-Radius und faerbt
 * den Ladezustand grau ein — beides widerspricht dem Layout der KI-Funktionen.
 */
export default defineComponent({
	name: 'ButtonFlat',
	emits: ['buttonclick'],
	props: {
		variant: {
			type: String,
			default: 'red', // red | outline | ai | white
		},
		loading: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},
});
</script>

<style lang="scss" scoped>
.button-flat {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 9px 16px;
	border: 0;
	border-radius: 6px;
	font-family: inherit;
	font-size: 16px;
	font-weight: 500;
	line-height: normal;
	cursor: pointer;
	transition: filter 0.2s ease;

	&:disabled {
		cursor: not-allowed;
	}

	&:not(.button-flat--loading):disabled {
		opacity: 0.4;
	}

	&:hover:not(:disabled) {
		filter: brightness(1.08);
	}
}

.button-flat__icon {
	display: flex;
	align-items: center;
	line-height: 0;
}

.button-flat--red {
	background-color: var(--el-color-red);
	color: #fff;
}

.button-flat--outline {
	background-color: transparent;
	border: 1px solid var(--el-color-primary);
	color: var(--el-color-primary);

	&:hover:not(:disabled) {
		filter: none;
		background-color: var(--el-color-lightgrey-1);
	}
}

/* Weiss auf blauem Grund — der Button im KI-Bereich der Entscheidung (Figma 67:478) */
.button-flat--white {
	background-color: #fff;
	color: var(--el-color-ai);

	&:hover:not(:disabled) {
		filter: none;
		background-color: var(--el-color-lightgrey-1);
	}
}

.button-flat--ai {
	position: relative;
	overflow: hidden;
	background-color: var(--el-color-ai);
	color: #fff;

	/* Wandernder Glanz waehrend der Generierung. Als Overlay statt background-position,
	   damit die Grundfarbe voll erhalten bleibt und der Button nicht grau wirkt. */
	&.button-flat--loading::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.55) 50%, transparent 100%);
		transform: translateX(-100%);
		animation: buttonFlatShine 1.6s ease-in-out infinite;
		pointer-events: none;
	}

	.button-flat__icon,
	.button-flat__label {
		position: relative;
		z-index: 1;
	}
}

@keyframes buttonFlatShine {
	from {
		transform: translateX(-100%);
	}
	to {
		transform: translateX(100%);
	}
}

@media (prefers-reduced-motion: reduce) {
	.button-flat--ai.button-flat--loading::after {
		animation: none;
		opacity: 0.35;
		transform: none;
	}
}
</style>
